import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useEarlyWarningsFilters } from '../../../hooks/useEarlyWarningsFilters';
import { FilterConfigValues, IconCode, TabFilterOption } from '../../../models/EarlyWarnings';
import styles from './ProjectsEligibleFirstUndisbursement.module.scss';
import { ProjectsElegibleTable } from './Table/Table';
import { getProjectsSimpleTableIndicator } from '../../../redux/actions/indicatorProjects';
import { PROJECTS_ELIGIBLE_FIRST_UNDISBURSEMENT } from '../../../services/sessionFiltersIndicatorDetailHelper';
import { EarlyWarningsFilters as ProjectsElegibleEarlyWarningsFilters } from '../EarlyWarningsFilters/EarlyWarningsFilters';
import { createFilterConfigComponent } from '../EarlyWarningsFilters/Util';
import { showEarlyWarning } from '../PortfolioDetailTablesUtil';
import { handleLoading } from '../../utils';
import FilterIndicators from '../../../models/FilterIndicators';
import { AppMenuTabs } from '../../MenuTabs/userTabsInterface';
import { setIndicatorResponse, setLoading } from '../../../redux/actions/dataTable';
import { getNewFilteredIndicators } from '../../../api/indicatorApi';
import { translateMultipleText } from '../../../services/stringUtil';
import { parseFilterToApiCall } from '../../../services/util';
import { emptyProjects } from '../../Portfolio/PortfolioUtil';
import { processIndicators } from '../../Portfolio/indicatorHelper';
import { arrayMove } from 'react-sortable-hoc';
import { addIndicators } from '../../../redux/actions/indicators';
import { ProjectProductTypes } from '../../../models/ProjectProductTypes';
import { useTranslation } from 'react-i18next';
import { resetIndicatorDetailFilter } from '../../../redux/actions/indicatorDetailFilters';
import { updateCreatingOrEditing } from '../../../redux/actions/menuTabs';
import Filter from '../../Portfolio/Filter/Filter';
import { setCurrentIndicator } from '../../../redux/actions/currentIndicator';
import ErrorMessageView from '../../Util/ErrorMessage/ErrorMessageView';

const filterConfigValues: FilterConfigValues[] = [
  { id: 0, filterIcon: IconCode.GREEN, filterLabel: 'less_than_one_month_after_eligibility' },
  { id: 1, filterIcon: IconCode.YELLOW, filterLabel: 'one_to_six_months_after_eligibility' },
  { id: 2, filterIcon: IconCode.RED, filterLabel: 'more_than_six_months_after_eligibility' },
];
const filterConfigComponent: TabFilterOption[] = createFilterConfigComponent(filterConfigValues);

const defaultSessionFilterObject = {
  filterConfigState: filterConfigComponent,
  changeProjectType: true,
};

export const ProjectsEligibleFirstUndisbursement = () => {
  const dispatch = useDispatch();
  const { container, earlyAlertsFilterContainer, bodyContainer, ErrorMessageStyle } = styles;
  const { currentIndicator } = useSelector<any, any>(state => state);
  const indicatorProjects = useSelector<any, any>(state => state.indicatorProjects);
  const { projectTypeFilterOnDetail } = useSelector<any, any>(state => state.indicatorDetailFilter);
  const { filterConfigState, handleEarlyWarningsFilter, handleProjectsQuantities } = useEarlyWarningsFilters({
    sessionKey: PROJECTS_ELIGIBLE_FIRST_UNDISBURSEMENT,
    defaultSessionFilterObject,
    projectTypeFilterOnDetail,
  });
  const [loadingData, setLoadingData] = useState<boolean>(false);
  const [hasError, setHasError] = useState(false)
  const { t } = useTranslation();

  // Filter

  const filterIndicators = useSelector<any, FilterIndicators>(state => state.filterIndicators);
  const stateProjectProductTypes = useSelector<any, FilterIndicators>(
    state => state.indicatorPayload.payload.projectProductTypes,
  );
  const menuTabs = useSelector<any, AppMenuTabs>(state => state.menuTabs);
  const portfolioSelectedTab = menuTabs.portfolioSelectedTab;
  const creatingOrEditing = menuTabs.creatingOrEditing;
  const [isLoading, setIsLoading] = React.useState(false);
  const [totalProjects, setTotalProjects] = React.useState(false);
  const prevIndicatorProjectsRef = useRef<any>(indicatorProjects);

  const effectiveProjectTypeFilters =
    Array.isArray(filterIndicators.common.projectTypeFilters) && filterIndicators.common.projectTypeFilters.length > 0
      ? filterIndicators.common.projectTypeFilters
      : Array.isArray(stateProjectProductTypes) && stateProjectProductTypes.length > 0
        ? stateProjectProductTypes
        : [];
  const parsedProjectProductTypes = parseFilterToApiCall(filterIndicators.common.projectTypeFilters).projectProductTypes;

  const projectTypeFilterChange = {
    ...filterIndicators,
    common: { ...filterIndicators.common, projectTypeFilters: effectiveProjectTypeFilters },
    projectTypeFilters: parsedProjectProductTypes,
  };

  const handleCommonFilterChange = async () => {
    dispatch(setLoading(true));
    setIsLoading(true);
    setHasError(false);

    try {
      const indicatorsToProcess = await getNewFilteredIndicators(
        {},
        { projectProductTypes: stateProjectProductTypes },
        {
          ...projectTypeFilterChange.common,
        },
      );
      dispatch(setIndicatorResponse(indicatorsToProcess));
      startProcessIndicators(
        indicatorsToProcess,
        translateMultipleText(` ${' '} ${portfolioSelectedTab.title}`, t),
        filterIndicators.common.projectTypeFilters,
      );
      setTotalProjects(emptyProjects(indicatorsToProcess.totalProjects));
    } catch (err) {
      console.error(err, 'Error fetching data from indicators');
      setHasError(true);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const startProcessIndicators = (inds, titleFromTab, projectTypeFilter = [ProjectProductTypes.Loan]) => {
    const result = processIndicators(inds, {
      titleFromTab,
      projectTypeFilter,
    });
    const elementSummary: any = result.find(elem => elem.id === 15);
    const indexSummary: number = result.indexOf(elementSummary);
    const sendEndSummary = arrayMove(result, indexSummary, result.length);

    dispatch(addIndicators(sendEndSummary));
    dispatch(
      setCurrentIndicator(sendEndSummary?.find(data => data.indicatorKey === 'PROJECTS_ELIGIBLE_FIRST_UNDISBURSEMENT')),
    );
  };

  useLayoutEffect(() => {
    if (!isLoading) {
      prevIndicatorProjectsRef.current = indicatorProjects;
      return;
    }
    if (prevIndicatorProjectsRef.current !== indicatorProjects) {
      setIsLoading(false);
    }
    prevIndicatorProjectsRef.current = indicatorProjects;
  }, [indicatorProjects, isLoading]);

  // const tabId0OrCreatingEditing = (menuTabs, creatingOrEditing) =>
  //   menuTabs.portfolioSelectedTab.tabId === 0 || creatingOrEditing;

  useEffect(() => {
    if (!effectiveProjectTypeFilters || effectiveProjectTypeFilters.length === 0) return;
    getFilteredSimpleIndicators(
      dispatch,
      currentIndicator.id,
      currentIndicator.indicatorKey,
      setLoadingData,
      projectTypeFilterChange,
      setHasError,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndicator.id, dispatch, projectTypeFilterOnDetail, filterIndicators.common]);

  useEffect(() => {
    if (!sessionStorage.getItem(PROJECTS_ELIGIBLE_FIRST_UNDISBURSEMENT)) {
      sessionStorage.setItem(PROJECTS_ELIGIBLE_FIRST_UNDISBURSEMENT, JSON.stringify(defaultSessionFilterObject));
    }
  }, []);

  useEffect(() => {
    if (
      filterIndicators.common.coReponsibleDivisions.length > 0 ||
      filterIndicators.common.countryId.length > 0 ||
      filterIndicators.common.divisionId.length > 0 ||
      filterIndicators.common.interestIssues.length > 0 ||
      filterIndicators.common.participantDivisions.length > 0 ||
      filterIndicators.common.regionId.length > 0 ||
      filterIndicators.common.sectors.length > 0 ||
      filterIndicators.common.teamLeaders.length > 0
    )
      handleCommonFilterChange();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterIndicators.common]);

  useEffect(() => {
    dispatch(resetIndicatorDetailFilter());
    if (
      !menuTabs.firstLoad &&
      // tabId0OrCreatingEditing(menuTabs, creatingOrEditing) &&
      filterIndicators.common.coReponsibleDivisions.length === 0 &&
      filterIndicators.common.countryId.length === 0 &&
      filterIndicators.common.divisionId.length === 0 &&
      filterIndicators.common.interestIssues.length === 0 &&
      filterIndicators.common.participantDivisions.length === 0 &&
      filterIndicators.common.regionId.length === 0 &&
      filterIndicators.common.sectors.length === 0 &&
      filterIndicators.common.teamLeaders.length === 0
    ) {
      handleCommonFilterChange();

      if (creatingOrEditing) {
        dispatch(updateCreatingOrEditing(false));
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterIndicators.common]);

  return (
    <div className={container}>
      {!hasError ? (
        <>
          {showEarlyWarning(projectTypeFilterOnDetail) && (
            <div className={earlyAlertsFilterContainer}>
              <ProjectsElegibleEarlyWarningsFilters
                filterSet={filterConfigState}
                handleFilter={handleEarlyWarningsFilter}
              />
            </div>
          )}
          <div className={bodyContainer}>
            <ProjectsElegibleTable
              projectsQuantities={handleProjectsQuantities}
              earlyWarningsTab={[
                filterConfigState[0].selected,
                filterConfigState[1].selected,
                filterConfigState[2].selected,
              ]}
              loading={isLoading}
            />
          </div>
        </>
      ) : (
        <div className={ErrorMessageStyle}>
          <ErrorMessageView
            title={t('title_error_message_view')}
            description={t('desc_error_message_tab')}
            reload={handleCommonFilterChange}
            isIndicator={true}
          />
        </div>
      )}

    </div>
  );
};

const getFilteredSimpleIndicators = (
  d,
  currentIndicatorId,
  currentIndicatorKey,
  setLoading,
  projectTypeFilterChange?,
  setHasError?,
) => {
  try {
    setHasError?.(false);
    return handleLoading(
      () => d(getProjectsSimpleTableIndicator(currentIndicatorId, currentIndicatorKey, projectTypeFilterChange)),
      setLoading,
    );
  } catch (error) {
    console.error(error, 'Error fetching filtered simple indicators');
    setHasError?.(true);
  }
};
